// Generate SVG-based North Indian chart images for reports
// This creates a simple, clean SVG representation of the chart

export interface PlanetPlacement {
  name: string;
  house: number;
  sign: number;
  degree: number;
  retrograde?: boolean;
}

export interface ChartSvgOptions {
  width?: number;
  height?: number;
  showDegrees?: boolean;
  showSigns?: boolean;
  title?: string;
}

const SIGN_NAMES = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const SIGN_ABBR = ['Ar', 'Ta', 'Ge', 'Cn', 'Le', 'Vi', 'Li', 'Sc', 'Sg', 'Cp', 'Aq', 'Pi'];

// Zodiac sign symbols
const SIGN_SYMBOLS: Record<number, string> = {
  1: '♈', 2: '♉', 3: '♊', 4: '♋', 5: '♌', 6: '♍',
  7: '♎', 8: '♏', 9: '♐', 10: '♑', 11: '♒', 12: '♓'
};

const PLANET_SYMBOLS: Record<string, string> = {
  Sun: '☉',
  Moon: '☽',
  Mars: '♂',
  Mercury: '☿',
  Jupiter: '♃',
  Venus: '♀',
  Saturn: '♄',
  Rahu: '☊',
  Ketu: '☋',
  Ascendant: 'Asc',
  Lagna: 'Asc'
};

const PLANET_ABBR: Record<string, string> = {
  Sun: 'Su',
  Moon: 'Mo',
  Mars: 'Ma',
  Mercury: 'Me',
  Jupiter: 'Ju',
  Venus: 'Ve',
  Saturn: 'Sa',
  Rahu: 'Ra',
  Ketu: 'Ke',
  Ascendant: 'As',
  Lagna: 'As'
};

/**
 * Generate a North Indian chart as SVG
 * @param placements - Array of planet placements
 * @param ascendantSign - Ascendant sign (1-12)
 * @param options - Rendering options
 * @returns SVG string
 */
export function generateNorthIndianChartSvg(
  placements: PlanetPlacement[],
  ascendantSign: number,
  options: ChartSvgOptions = {}
): string {
  const {
    width = 400,
    height = 400,
    showDegrees = true,
    showSigns = true,
    title = ''
  } = options;

  // Group planets by house
  const houseContents: Record<number, PlanetPlacement[]> = {};
  for (let i = 1; i <= 12; i++) {
    houseContents[i] = [];
  }
  placements.forEach(p => {
    if (p.house >= 1 && p.house <= 12) {
      houseContents[p.house].push(p);
    }
  });

  // North Indian chart house positions (percentage-based) - adjusted for better spacing
  const houseBoxes = [
    { house: 1, x: 35, y: 10, w: 30, h: 18 },   // Top center
    { house: 2, x: 14, y: 4, w: 24, h: 14 },    // Top left
    { house: 3, x: 4, y: 16, w: 14, h: 18 },    // Left upper
    { house: 4, x: 10, y: 37, w: 28, h: 20 },   // Left center
    { house: 5, x: 4, y: 66, w: 14, h: 18 },    // Left lower
    { house: 6, x: 14, y: 82, w: 24, h: 14 },   // Bottom left
    { house: 7, x: 35, y: 72, w: 30, h: 18 },   // Bottom center
    { house: 8, x: 62, y: 82, w: 24, h: 14 },   // Bottom right
    { house: 9, x: 82, y: 66, w: 14, h: 18 },   // Right lower
    { house: 10, x: 62, y: 37, w: 28, h: 20 },  // Right center
    { house: 11, x: 82, y: 16, w: 14, h: 18 },  // Right upper
    { house: 12, x: 62, y: 4, w: 24, h: 14 }    // Top right
  ];

  // Diamond outline points
  const diamondPath = `M ${width/2},${height*0.03} L ${width*0.97},${height/2} L ${width/2},${height*0.97} L ${width*0.03},${height/2} Z`;

  // Build SVG
  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;
  
  // Add defs for subtle gradient
  svg += `<defs>
    <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#fefefe;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#f8f8f8;stop-opacity:1" />
    </linearGradient>
  </defs>`;
  
  // Background with gradient
  svg += `<rect width="${width}" height="${height}" fill="url(#bgGrad)"/>`;
  
  // Title if provided
  if (title) {
    svg += `<text x="${width/2}" y="24" text-anchor="middle" font-size="18" font-weight="600" fill="#222" font-family="Arial, sans-serif">${escapeXml(title)}</text>`;
  }

  // Diamond outline with shadow effect
  svg += `<path d="${diamondPath}" fill="white" stroke="#2c3e50" stroke-width="2.5" stroke-linejoin="miter"/>`;

  // Internal divisions (cross pattern)
  svg += `<line x1="${width/2}" y1="${height*0.03}" x2="${width/2}" y2="${height*0.97}" stroke="#34495e" stroke-width="1.5"/>`;
  svg += `<line x1="${width*0.03}" y1="${height/2}" x2="${width*0.97}" y2="${height/2}" stroke="#34495e" stroke-width="1.5"/>`;
  
  // Diagonal lines
  svg += `<line x1="${width*0.03}" y1="${height/2}" x2="${width/2}" y2="${height*0.03}" stroke="#34495e" stroke-width="1.5"/>`;
  svg += `<line x1="${width/2}" y1="${height*0.03}" x2="${width*0.97}" y2="${height/2}" stroke="#34495e" stroke-width="1.5"/>`;
  svg += `<line x1="${width*0.97}" y1="${height/2}" x2="${width/2}" y2="${height*0.97}" stroke="#34495e" stroke-width="1.5"/>`;
  svg += `<line x1="${width/2}" y1="${height*0.97}" x2="${width*0.03}" y2="${height/2}" stroke="#34495e" stroke-width="1.5"/>`;

  // Render each house
  houseBoxes.forEach(box => {
    const planets = houseContents[box.house];
    const signNum = ((ascendantSign - 1 + box.house - 1) % 12) + 1;
    
    // Calculate actual pixel coordinates
    const x = (box.x / 100) * width;
    const y = (box.y / 100) * height;
    const w = (box.w / 100) * width;
    const h = (box.h / 100) * height;
    
    // Sign symbol and number (elegant corner display)
    if (showSigns) {
      const signSymbol = SIGN_SYMBOLS[signNum] || signNum;
      svg += `<text x="${x + 4}" y="${y + 14}" font-size="16" fill="#7f8c8d" font-family="Arial, sans-serif">${signSymbol}</text>`;
      svg += `<text x="${x + 22}" y="${y + 12}" font-size="9" fill="#95a5a6" font-family="Arial, sans-serif">${signNum}</text>`;
    }

    // Planets in this house
    let yOffset = y + (showSigns ? 22 : 14);
    planets.forEach((p, idx) => {
      const symbol = PLANET_SYMBOLS[p.name] || '';
      const abbr = PLANET_ABBR[p.name] || p.name.substring(0, 2);
      const retro = p.retrograde ? '℞' : '';
      const deg = showDegrees && p.degree ? ` ${Math.floor(p.degree)}°` : '';
      
      // Planet with symbol and abbreviation
      svg += `<text x="${x + 6}" y="${yOffset}" font-size="13" font-weight="500" fill="#2c3e50" font-family="Arial, sans-serif">`;
      svg += `${symbol ? symbol + ' ' : ''}${escapeXml(abbr)}${retro}`;
      if (deg) {
        svg += `</text><text x="${x + 6}" y="${yOffset}" font-size="10" fill="#7f8c8d" font-family="Arial, sans-serif" dx="35">${deg}</text>`;
      } else {
        svg += `</text>`;
      }
      
      yOffset += 14;
      
      // Prevent overflow
      if (yOffset > y + h - 4) return;
    });
  });

  svg += `</svg>`;
  return svg;
}

/**
 * Convert SVG string to data URL for embedding in reports
 */
export function svgToDataUrl(svg: string): string {
  const encoded = encodeURIComponent(svg);
  return `data:image/svg+xml,${encoded}`;
}

/**
 * Helper to escape XML special characters
 */
function escapeXml(unsafe: string): string {
  return unsafe.replace(/[<>&'"]/g, c => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case "'": return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}

/**
 * Generate chart images for D1, D9, D10 from astroData
 */
export function generateChartImages(
  placements: Record<string, { house: number; sign: number; siderealLongitudeDeg: number; retrograde?: boolean }>,
  ascendantSign: number,
  vargas?: {
    D9?: Record<string, { house: number; sign: number }>;
    D10?: Record<string, { house: number; sign: number }>;
  }
): { d1?: string; d9?: string; d10?: string } {
  const result: { d1?: string; d9?: string; d10?: string } = {};

  // Generate D1 (Rashi) chart
  const d1Placements: PlanetPlacement[] = Object.entries(placements).map(([name, data]) => ({
    name,
    house: data.house,
    sign: data.sign,
    degree: data.siderealLongitudeDeg % 30,
    retrograde: data.retrograde
  }));
  
  const d1Svg = generateNorthIndianChartSvg(d1Placements, ascendantSign, {
    title: 'D1 - Rashi Chart',
    width: 500,
    height: 500
  });
  result.d1 = svgToDataUrl(d1Svg);

  // Generate D9 (Navamsa) chart if available
  if (vargas?.D9) {
    const d9Placements: PlanetPlacement[] = Object.entries(vargas.D9).map(([name, data]) => ({
      name,
      house: data.house,
      sign: data.sign,
      degree: 0
    }));
    const d9Svg = generateNorthIndianChartSvg(d9Placements, ascendantSign, {
      title: 'D9 - Navamsa',
      width: 500,
      height: 500,
      showDegrees: false
    });
    result.d9 = svgToDataUrl(d9Svg);
  }

  // Generate D10 (Dasamsa) chart if available
  if (vargas?.D10) {
    const d10Placements: PlanetPlacement[] = Object.entries(vargas.D10).map(([name, data]) => ({
      name,
      house: data.house,
      sign: data.sign,
      degree: 0
    }));
    const d10Svg = generateNorthIndianChartSvg(d10Placements, ascendantSign, {
      title: 'D10 - Dasamsa',
      width: 500,
      height: 500,
      showDegrees: false
    });
    result.d10 = svgToDataUrl(d10Svg);
  }

  return result;
}
